import React from 'react';
import {
  FiUsers,
  FiDollarSign,
  FiActivity,
  FiLoader,
} from 'react-icons/fi';
import MonthlyMemberAcquisitionChart from './MonthlyMemberAcquisitionChart';
import RevenuePerMemberTrendChart from './RevenuePerMemberTrendChart';
import AvgServicesPerMemberChart from './AvgServicesPerMemberChart';
import MemberSegmentsByTenureChart from './MemberSegmentsByTenureChart';
import LiveMetricCard from './LiveMetricCard';
import DataSourceBadge from './DataSourceBadge';
import DataSourceNotConnected from './DataSourceNotConnected';
import DataSufficiencyWrapper from './DataSufficiencyWrapper';
import { useInsightsData } from '../../../hooks/useInsightsData';
import { NOT_CONNECTED_REGISTRY } from '../../../types/insightsTypes';

interface ProgressBarProps {
  title: string;
  current: number;
  target: number;
  format: 'number' | 'currency' | 'percentage';
}

const ProgressBar: React.FC<ProgressBarProps> = ({ title, current, target, format }) => {
  const percentage = Math.min((current / target) * 100, 100);

  const formatValue = (value: number) => {
    switch (format) {
      case 'currency':
        return `$${value.toLocaleString()}`;
      case 'percentage':
        return `${value}%`;
      default:
        return value.toLocaleString();
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <h5 className="text-slate-700 font-medium">{title}</h5>
        <span className="text-slate-600 text-sm">
          {formatValue(current)} / {formatValue(target)}
        </span>
      </div>
      <div className="w-full bg-slate-200 rounded-full h-3">
        <div
          className="bg-slate-800 h-3 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
};

const MemberAcquisition: React.FC = () => {
  const { data, loading, error } = useInsightsData();
  const acq = data?.acquisition;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <FiLoader className="animate-spin text-slate-400 mr-3" size={20} />
        <span className="text-slate-500">Loading acquisition data…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700 font-medium">Failed to load insights data</p>
        <p className="text-red-500 text-sm mt-1">{error.message}</p>
      </div>
    );
  }

  if (!acq) return null;

  // Determine if we have any live member data for the progress bar
  const totalMemberVal = acq.total_member_base.value;
  const memberGrowthPct = acq.total_member_base.trend;

  return (
    <div className="space-y-6">
      {/* Key Metrics Grid */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-base sm:text-lg font-medium text-slate-700">
            Member acquisition and growth metrics
          </h3>
          <DataSourceBadge status="partial" source="lender_acquisition_v" />
        </div>
        <DataSufficiencyWrapper
          sufficiency={acq.total_member_base.sufficiency}
          sampleSize={acq.total_member_base.sample_size}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <LiveMetricCard
              title="New Members (This Month)"
              metric={acq.new_members}
              icon={<FiUsers className="text-slate-600" size={20} />}
              format="number"
              target="300"
              metricKey="acquisition.new_members"
            />
            <LiveMetricCard
              title="Total Member Base"
              metric={acq.total_member_base}
              icon={<FiUsers className="text-slate-600" size={20} />}
              format="number"
              target="15,000"
              metricKey="acquisition.total_member_base"
            />
            <LiveMetricCard
              title="Revenue per Member"
              metric={acq.revenue_per_member}
              icon={<FiDollarSign className="text-slate-600" size={20} />}
              format="currency"
              requiredIntegration="core_banking"
              metricKey="acquisition.revenue_per_member"
            />
            <LiveMetricCard
              title="Avg Services per Member"
              metric={acq.avg_services}
              icon={<FiActivity className="text-slate-600" size={20} />}
              format="number"
              requiredIntegration="core_banking"
              metricKey="acquisition.avg_services"
            />
          </div>
        </DataSufficiencyWrapper>
      </div>

      {/* Monthly Acquisition (PARTIAL) & Revenue Trend (NOT_CONNECTED) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base sm:text-lg font-medium text-slate-700">Monthly New Member Acquisition</h4>
            <DataSourceBadge
              status={acq.monthly_acquisition_chart.status}
              source="lender_acquisition_v"
              compact
            />
          </div>
          <div className="h-64 sm:h-80">
            <MonthlyMemberAcquisitionChart series={acq.monthly_acquisition_chart} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Revenue per Member Trend</h4>
          <RevenuePerMemberTrendChart />
        </div>
      </div>

      {/* Services (NOT_CONNECTED) & Segments (NOT_CONNECTED) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Average Services per Member</h4>
          <AvgServicesPerMemberChart />
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
          <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Members by Length of Membership</h4>
          <MemberSegmentsByTenureChart />
        </div>
      </div>

      {/* Progress Towards Annual Goals — live for members, NOT_CONNECTED for revenue */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
        <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-6">Progress Towards Annual Goals</h4>
        <div className="space-y-6">
          {/* Member Base Growth — LIVE if we have trend data */}
          {memberGrowthPct != null ? (
            <ProgressBar
              title="Member Base Growth"
              current={Math.abs(memberGrowthPct)}
              target={15}
              format="percentage"
            />
          ) : (
            <div>
              <h5 className="text-slate-700 font-medium mb-2">Member Base Growth</h5>
              <DataSourceNotConnected info={NOT_CONNECTED_REGISTRY.none} metricName="Member Base Growth" inline />
            </div>
          )}

          {/* Revenue per Member — NOT_CONNECTED */}
          <div>
            <h5 className="text-slate-700 font-medium mb-2">Revenue per Member</h5>
            <DataSourceNotConnected info={NOT_CONNECTED_REGISTRY.core_banking} metricName="Revenue per Member" inline />
          </div>

          {/* Total Members — LIVE if available */}
          {totalMemberVal != null ? (
            <ProgressBar
              title="Total Members"
              current={totalMemberVal}
              target={15000}
              format="number"
            />
          ) : (
            <div>
              <h5 className="text-slate-700 font-medium mb-2">Total Members</h5>
              <DataSourceNotConnected info={NOT_CONNECTED_REGISTRY.none} metricName="Total Members" inline />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemberAcquisition;